import React, { ChangeEvent, Fragment, useRef, useState } from "react";
import { FaTimes, FaCheckCircle } from "react-icons/fa";
import "./EV.css";
import axios, { AxiosResponse } from "axios";
import { Col, Row, Form, Button, Modal } from "react-bootstrap";
import { Election } from "./Election";
import Private from "./responses/Private";
import ErrorResponse from "./responses/ErrorResponse";
import { ResponseVoter, Voter } from "./responses/Voter";
import Found from "./responses/Found";
import NotFound from "./responses/NotFound";
import { ObjectMap } from "./ObjectMap";
import { getMessage } from "./util";
import { Helmet } from "react-helmet";

export type ConsultaProps = {
    election: Election;
    messages: null | ObjectMap<string>;
};

export const Consulta = (props: ConsultaProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [state, setState] = useState<string | null>(null);
    const [voter, setVoter] = useState<Voter | null>(null);

    const [runClasses, setRunClasses] = useState<string>("");
    const [run, setRun] = useState<string | undefined>(undefined);

    const [showError, setShowError] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);

    const site_key: string = process.env.REACT_APP_ENTERPRISE_RECAPTCHA_SITE!;
    const site_key2: string = process.env.REACT_APP_ENTERPRISE_RECAPTCHA_SITE2!;

    const captcha2 = useRef<HTMLDivElement>(null);

    const volver = () => {
        setState(null);
        setVoter(null);
        setRun(undefined);
        setRunClasses("");
    };

    const getDv = (rut: string) => {
        let suma: number = 0;
        for (let i: number = rut.length - 1; i >= 0; i += -1) {
            suma =
                suma + parseInt(rut.charAt(i), 10) * (2 + ((rut.length - 1 - i) % 6));
        }
        suma = 11 - (suma % 11);
        switch (suma) {
            case 10:
                return "K";
            case 11:
                return 0;
            default:
                return suma;
        }
    };

    const validateRut = (rut: string) => {
        var cdv, noFormat;
        noFormat = rut.replace(/[^0-9kK]/g, "").toUpperCase();
        cdv = getDv(noFormat.substring(0, noFormat.length - 1)).toString();
        return (
            noFormat.charAt(noFormat.length - 1).toUpperCase() === cdv.toUpperCase()
        );
    };

    const renderCaptcha = () => {
        if (run == null || !validateRut(run)) {
            setRunClasses("is-invalid");
            return;
        }

        setLoading(true);

        window.grecaptcha.enterprise.render(captcha2.current!, {
            sitekey: site_key2,
            callback: async (token) => {
                setState(null);
                await connect(encodeURI(run.replace(/\./g, "")), true, token);
                setLoading(false);
            },
        });
    };

    const connect = async (
        identity: string,
        rechallenge: boolean,
        token: string | null
    ) => {
        try {
            const voter: AxiosResponse<ResponseVoter> = await axios({
                method: "post",
                url:
                    process.env.REACT_APP_API_URL +
                    "/election/" +
                    props.election._id +
                    "/voter/" +
                    identity,
                data: {
                    recaptcha: token,
                    rechallenge: rechallenge,
                },
            });

            if (voter.data.state === "ok") {
                setVoter(voter.data.voterInfo!);
            }
        } catch (error) {
            switch (error.response.status) {
                case 400:
                    if (error.response.data && error.response.data.state === "captcha") {
                        setLoading(false);
                        if (rechallenge) setState("nocaptcha");
                        else setState("captcha");
                        break;
                    }
                    setState("error");
                    break;
                case 401:
                    setState("unpublish");
                    break;
                case 404:
                    setState("notfound");
                    break;
                default:
                    setState("error");
                    break;
            }
        } finally {
            setLoading(false);
        }
    };

    const clean = () => {
        setRun(undefined);
        setState(null);
        setRunClasses("");
        setLoading(false);
    };

    const loadVoter = async () => {
        if (run == null) {
            setRunClasses("is-invalid");
            return;
        }

        let cleanRun = run;

        switch (
            props.election.configuration.identification_field.split("+", 2)[0]
            ) {
            case "run":
                cleanRun = run.replace(/\./g, "").trim();
                if (!validateRut(cleanRun)) {
                    setRunClasses("is-invalid");
                    return;
                }
                break;
            case "oauth2":
                cleanRun = run.replace(/\./g, "").trim();
                if (!validateRut(cleanRun)) {
                    setRunClasses("is-invalid");
                    return;
                }
                break;
            case "email":
                cleanRun = run.trim();
                break;
            default:
                cleanRun = run.trim();
                break;
        }

        setLoading(true);

        if (props.election.configuration.advanced_config.captcha) {
            window.grecaptcha.enterprise.ready(async () => {
                window.grecaptcha.enterprise
                    .execute(site_key, { action: "ask_data" })
                    .then((token) => {
                        connect(encodeURI(cleanRun), false, token);
                    });
            });
        } else {
            await connect(encodeURI(cleanRun), false, null);
        }
        setLoading(false);
    };

    const getIdentificationInput = () => {
        switch (
            props.election.configuration.identification_field.split("+", 2)[0]
            ) {
            case "run":
                return (
                    <Form.Group controlId="run">
                        <Form.Label className="my-4">
                            {getMessage("excerpt_run", props.messages)}
                        </Form.Label>
                        <Form.Control
                            onChange={handleRutInputChange}
                            type="text"
                            name="run"
                            onKeyDown={handleKeyDown}
                            placeholder="Ej: 12345678-9"
                            maxLength={12}
                            className={runClasses}
                        />
                        <Form.Control.Feedback type="invalid">
                            {getMessage("wrong_id", props.messages)}
                        </Form.Control.Feedback>
                    </Form.Group>
                );
            case "oauth2":
                return (
                    <Form.Group controlId="run">
                        <Form.Label className="my-4">
                            {getMessage("excerpt_run", props.messages)}
                        </Form.Label>
                        <Form.Control
                            onChange={handleRutInputChange}
                            type="text"
                            name="run"
                            onKeyDown={handleKeyDown}
                            placeholder="Ej: 12345678-9"
                            maxLength={12}
                            className={runClasses}
                        />
                        <Form.Control.Feedback type="invalid">
                            {getMessage("wrong_id", props.messages)}
                        </Form.Control.Feedback>
                    </Form.Group>
                );
            case "toc_clave_unica":
                return (
                    <Form.Group controlId="run">
                        <Form.Label className="my-4">
                            {getMessage("excerpt_run", props.messages)}
                        </Form.Label>
                        <Form.Control
                            onChange={handleRutInputChange}
                            type="text"
                            name="run"
                            onKeyDown={handleKeyDown}
                            placeholder="Ej: 12345678-9"
                            maxLength={12}
                            className={runClasses}
                        />
                        <Form.Control.Feedback type="invalid">
                            {getMessage("wrong_id", props.messages)}
                        </Form.Control.Feedback>
                    </Form.Group>
                );
            case "email":
                return (
                    <Form.Group controlId="run">
                        <Form.Label className="my-4">
                            {getMessage("nro-documento", props.messages)}
                        </Form.Label>
                        <div className="input-group">
                            <Form.Control
                                onChange={handleEmailInputChange}
                                onBlur={handleEmailBlur} // Validar cuando el campo pierde el foco
                                type="email"
                                autoComplete="email"
                                name="run"
                                value={run} // Mantener el valor del input actualizado
                                placeholder="Ej: some@example.com"
                                className={runClasses} // Aplicar las clases de validación
                            />
                            {showError ? (
                                <span
                                    className="input-group-text text-danger"
                                    style={{ cursor: "pointer", position: "relative" }}
                                    onMouseEnter={() => setShowTooltip(true)} // Mostrar tooltip al pasar el mouse
                                    onMouseLeave={() => setShowTooltip(false)} // Ocultar tooltip al salir
                                >
                  <FaTimes /> {/* Ícono de error */}
                                    {showTooltip && (
                                        <div
                                            style={{
                                                position: "absolute",
                                                bottom: "100%", // Posición encima del ícono
                                                right: "0",
                                                textAlign: "justify",
                                                backgroundColor: "#f8d7da",
                                                whiteSpace: "break-spaces",
                                                width: "280px",
                                                color: "#721c24",
                                                padding: "5px 10px",
                                                borderRadius: "4px",
                                                zIndex: 1000,
                                                marginBottom: "5px",
                                            }}
                                        >
                                            Ingrese nuevamente su correo some@example.cl
                                        </div>
                                    )}
                </span>
                            ) : runClasses === "is-valid" ? (
                                <span
                                    className="input-group-text text-success"
                                    style={{ cursor: "pointer" }}
                                >
                  <FaCheckCircle /> {/* Ícono de ticket verde */}
                </span>
                            ) : null}
                        </div>
                        <Form.Control.Feedback type="invalid">
                            {getMessage("wrong_id", props.messages)}
                        </Form.Control.Feedback>
                    </Form.Group>
                );
            default:
                return (
                    <Form.Group controlId="run">
                        <Form.Label className="my-4">
                            Ingrese su número de documento registrado
                        </Form.Label>
                        <Form.Control
                            type="text"
                            name="run"
                            value={run}
                            onChange={handleIdInputChange}
                            onKeyDown={handleKeyDown}
                        />
                    </Form.Group>
                );
        }
    };

    const verifyAndFixRut = (target: any) => {
        var cdv, dv, noFormat, rut, withFormat;

        noFormat = target.value.replace(/[^0-9kK]/g, "").toUpperCase();
        dv = noFormat.charAt(noFormat.length - 1);
        rut = noFormat.substring(0, noFormat.length - 1);
        withFormat = "";
        while (rut.length > 3) {
            withFormat = "." + rut.substr(rut.length - 3) + withFormat;
            rut = rut.substring(0, rut.length - 3);
        }
        withFormat = rut + withFormat + "-" + dv.toUpperCase();
        if (noFormat.length > 1) {
            target.value = withFormat;
        } else {
            target.value = noFormat;
        }
        cdv = getDv(noFormat.substring(0, noFormat.length - 1)).toString();
        if (dv.toUpperCase() === cdv.toUpperCase()) {
            setRunClasses("is-valid");
            setRun(withFormat);
        } else {
            setRunClasses("");
            setRun(withFormat);
        }
    };

    const handleEmailBlur = () => {
        let correctedEmail = (run || "").trim(); // Asegurarse de que run sea una cadena

        // Detectar si falta el @
        const missingAtSymbol = !correctedEmail.includes("@");

        if (missingAtSymbol) {
            setShowError(true); // Mostrar el error si falta el @
            setRunClasses("is-invalid"); // Marcar el campo como inválido
        } else {
            // Limpiar el estado de error y marcar como válido
            setShowError(false);
            setRunClasses("is-valid"); // Marcar el campo como válido
        }
    };

    const handleRutInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        verifyAndFixRut(event.currentTarget);
    };

    const handleEmailInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setRun(inputValue); // Actualizamos run
        setShowError(false); // Reiniciamos el estado de error cuando se escribe
        setRunClasses(""); // Reiniciamos las clases cuando se escribe
        setShowTooltip(false); // Ocultar el tooltip al escribir
    };

    const handleIdInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const rawValue = e.target.value; // Obtiene el valor ingresado
        const value = rawValue.toUpperCase(); // Convierte a mayúsculas automáticamente
        const regex = /^[A-Z0-9]*$/; // Solo letras mayúsculas y números

        // Filtrar caracteres inválidos
        const filteredValue = value.split('').filter((char) => regex.test(char)).join('');
        setRun(filteredValue); // Actualiza el estado con el valor filtrado
    };


    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setRun(event.currentTarget.value.trim());
    };

    const handleKeyDown = (event: any) => {
        if (event.key === "Enter" && event.shiftKey === false) {
            event.preventDefault();
        }
    };

    return (
        <Fragment>
            <Modal
                show={state === "captcha"}
                onHide={clean}
                onEntered={renderCaptcha}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {getMessage("captcha_title", props.messages)}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p className={"lead"}>
                        {getMessage("captcha.try.message", props.messages)}
                    </p>
                    <div ref={captcha2}></div>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={clean}>
                        {getMessage("close", props.messages)}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={state === "nocaptcha"} onHide={clean}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {getMessage("captcha_title", props.messages)}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p className={"lead"}>
                        {getMessage("captcha.fail.message", props.messages)}
                    </p>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={clean}>
                        {getMessage("close", props.messages)}
                    </Button>
                </Modal.Footer>
            </Modal>

            {state === "unpublish" && (
                <Private messages={props.messages} election={props.election} />
            )}
            {state === "error" && (
                <ErrorResponse messages={props.messages} volver={volver} />
            )}
            {state === "notfound" && (
                <NotFound messages={props.messages} volver={volver} />
            )}
            {voter && (
                <Found
                    messages={props.messages}
                    election={props.election}
                    voter={voter}
                    volver={volver}
                />
            )}

            {props.election.configuration.advanced_config.captcha && (
                <Helmet>
                    <script
                        src={
                            "https://www.google.com/recaptcha/enterprise.js?render=" +
                            site_key
                        }
                    ></script>
                </Helmet>
            )}

            {!(state || voter) && (
                <Row className={"justify-content-center"}>
                    <Col xs={12} lg={10} className="mt-2 px-4 px-sm-2">
                        <div className={"consulta-text mx-3"}>
                            <h5 className="text-start mt-2 mb-4">
                                <b>
                                    Consulta si estás habilitad@ para votar
                                </b>
                            </h5>
                            <ol className="ms-2">
                                <li className="fw-bold">
                                    <span className={"fw-normal"}>
                                        Ingresa tu número de documento registrado en la Mesa Nacional de la Sociedad Civil para las Migraciones (MNSCM).
                                    </span>
                                </li>
                                <li className="fw-bold">
                                    <span className={"fw-normal"}>
                                        Puedes ingresar uno de los siguientes tipos de documento, según corresponda:
                                    </span>
                                </li>
                                <ul>
                                    <li>Cédula de Ciudadanía</li>
                                    <li>Permiso por protección Temporal (PPT)</li>
                                    <li>Visa de migrante (Tipo M)</li>
                                    <li>Visa de Residente (Tipo R)</li>
                                    <li>Salvoconducto de permanencia por refugio (SC-2)</li>
                                </ul>
                                <p className={"py-3 m-0 fw-bold"}>Instrucciones importantes:</p>
                                <ul className={"pb-4"}>
                                    <li>Solo ingresa letras en caso de que el documento con el que te inscribiste las tenga.</li>
                                    <li>No utilices guiones, espacios, puntos ni otros signos.</li>
                                </ul>
                                <li className="fw-bold">
                                    <span className={"fw-normal"}>Si estás habilitado(a), podrás participar en la Primera Selección de Delegados(as) de la Mesa Nacional de la Sociedad Civil para las Migraciones, periodo 2025-2026. En caso contrario, revisa cuidadosamente el número de documento que ingresaste y vuelve a intentarlo.</span>
                                </li>
                            </ol>
                            <p>
                                <b>Nota:</b> Las personas habilitadas para votar en este proceso son todas aquellas inscritas en la Mesa antes del 19 de noviembre. Para más información puedes consultar el&nbsp;
                                <a className="text-nowrap" href="https://rumboalamesa.colombianosune.com/assets/docs/Resolucio%CC%81n%202996%20del%2025%20de%20abril%20de%202024%20-%20Por%20la%20cual%20se%20reglamenta%20la%20convocatoria%20a%20la%20Mesa%20Nacional%20de%20la%20Sociedad%20Civil%20para%20las%20Migraciones%20(2).pdf">Artículo 17 de la Resolución 2996 de 2024</a>.
                            </p>
                            <p>
                                Si tienes alguna duda o necesitas asistencia,&nbsp;
                                <a href="https://evoting.typeform.com/to/VKwDaENo">diligenciar el formulario de soporte disponible</a>.
                            </p>
                        </div>
                        {loading ? (
                            <div className={"spinner"}/>
                        ) : (
                            <Form>
                                {getIdentificationInput()}
                                <Row>
                                    <Col sm={6} className={"text-right my-3"}>
                                        <Button
                                            className="EvColorBlue text-white my-0 me-2"
                                            onClick={loadVoter}
                                            variant="primary"
                                        >
                                            {getMessage("ask_button", props.messages)}
                                        </Button>
                                        <Button
                                            className="EvColorGrisLight my-0 ms-2"
                                            href={props.election.configuration.home_url}
                                            variant="light"
                                        >
                                            {getMessage("exit_button", props.messages)}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        )}
                    </Col>
                </Row>
            )}
        </Fragment>
    );
};

export default Consulta;
